import { LEFT_OF_EXPORT_RIBBON_CLASS, useIsExportRibbonShown } from 'components/export-ribbon/ExportRibbon';
import { ChevronDownIcon, XIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useElevation } from 'hooks/useUiTheme';
import { m } from 'motion/react';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { useCreateDialogRequest } from './CreateDialogContext';
import { CREATE_DIALOG_FRAME, MORPH, useFollowFrame } from './CreateDialogFrame';
import type { FieldValues } from 'react-hook-form';
import { createRecord } from './recordTypes';

function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * The name typed for the record, if the values hold one, else the case picked for a record that has no name of
 * its own, a timecard or an expense.
 *
 * ponytail: knows the fields most forms call a name. A record whose name is called otherwise (a project, a
 * group, a training) shows where it started instead; give the record types a name field if that matters.
 */
function nameIn(values: FieldValues | undefined): string | null {
    return (
        text(values?.name) ||
        [text(values?.firstName), text(values?.lastName)].filter(Boolean).join(' ') ||
        text(values?.matter?.fullName) ||
        null
    );
}

/**
 * The tab's box, which its frame and its content share. On the bottom edge, on the export ribbon's layer: over the
 * page, under every dialog, popup and toast. Under 768px it spans the edge.
 */
const TAB_BOX_CLASS = cn(
    'fixed end-[calc(1.5rem+var(--ai-side-dock-width,0px))] bottom-0 z-[calc(var(--z-floating-ai-launcher)-1)] h-12 w-[380px]',
    'max-sm:start-0 max-sm:end-0 max-sm:h-[60px] max-sm:w-auto'
);

/** The content over the frame, with the tab's edge. */
const TAB_CLASS = cn(
    'text-popover-foreground border-border-menu flex items-center gap-1 rounded-t-[10px] border border-b-0 ps-1 pe-1.5',
    'max-sm:rounded-t-xl max-sm:border-x-0 max-sm:px-2'
);

/** `rounded-t-[10px]`, `rounded-t-xl` on the sheet. */
const TAB_RADIUS = 10;
const SHEET_TAB_RADIUS = 14;

/**
 * Arriving, the tab is over the dialog's layer, so the backdrop fading out does not dim its frame on the way down;
 * leaving, it fades out as the dialog's frame grows back.
 */
type TabPhase = 'arriving' | 'shown' | 'leaving' | null;

/**
 * The create dialog while it is minimised: a tab that says what is being created, by the name typed for it or
 * else by where it started, and shows the dialog again, with a close button of its own, which asks what the
 * dialog's asks. It sits left of the export ribbon while that shows. It is portalled to the page's end as the
 * dialog is, whose popup is hidden meanwhile.
 *
 * With motion allowed its frame carries the id the dialog's frame does, and is mounted only while minimised:
 * minimising, the frame shrinks out of the dialog's box into the tab's as the content fades in inside it. Restored,
 * the content fades out inside the frame growing back. Under reduced motion the frame carries no id, stays, and
 * fades with the content.
 */
export function CreateDialogTab({ onReopen }: { onReopen: () => void }) {
    const intl = useIntl();
    const request = useCreateDialogRequest();
    const besideExports = useIsExportRibbonShown();
    const reduced = useReducedMotion();
    const isSheet = useIsMobile();
    const shadow = useElevation('menu');
    const minimised = request?.minimised ?? false;
    const contentRef = useRef<HTMLDivElement>(null);
    const [phase, setPhase] = useState<TabPhase>(null);
    const [wasMinimised, setWasMinimised] = useState(false);
    if (minimised !== wasMinimised) {
        setWasMinimised(minimised);
        setPhase(minimised ? 'arriving' : 'leaving');
    }
    useFollowFrame(contentRef, minimised, !reduced);

    if (!request || phase === null) {
        return null;
    }
    const { titleId, icon: Icon, tileClass } = createRecord(request.record);
    const title = intl.formatMessage({ id: titleId });
    // Read once, as the tab is drawn: nothing is typed into a hidden form.
    const detail = nameIn(request.adapter?.getValues() ?? request.form?.getValues()) ?? request.chipName;

    const boxClass = cn(
        TAB_BOX_CLASS,
        besideExports && LEFT_OF_EXPORT_RIBBON_CLASS,
        phase === 'arriving' && 'z-[calc(var(--z-modal)+1)]'
    );
    const radius = isSheet ? SHEET_TAB_RADIUS : TAB_RADIUS;
    const fade = {
        initial: { opacity: 0 },
        animate: { opacity: minimised ? 1 : 0 },
        transition: reduced ? MORPH.still : minimised ? MORPH.minimise : MORPH.restore,
    };

    return createPortal(
        <>
            {/* Under reduced motion nothing morphs: the frame fades with the content. Keyed and laid out as the
                dialog's frame is. */}
            {reduced || minimised ? (
                <m.div
                    key={reduced ? 'still' : 'morph'}
                    data-slot={CREATE_DIALOG_FRAME}
                    layoutId={reduced ? undefined : CREATE_DIALOG_FRAME}
                    layoutDependency={minimised}
                    className={cn(boxClass, 'bg-popover')}
                    style={{
                        borderTopLeftRadius: radius,
                        borderTopRightRadius: radius,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        boxShadow: shadow,
                    }}
                    {...(reduced ? fade : { transition: MORPH.minimise })}
                />
            ) : null}
            <m.div
                ref={contentRef}
                className={cn(boxClass, TAB_CLASS)}
                aria-hidden={phase === 'leaving' || undefined}
                inert={phase === 'leaving'}
                {...fade}
                onAnimationComplete={() =>
                    setPhase((current) => (current === 'arriving' ? 'shown' : current === 'leaving' ? null : current))
                }
            >
                <Button
                    variant="ghost"
                    className="h-10 min-w-0 flex-1 justify-start gap-2.5 px-2 font-normal max-sm:h-11"
                    aria-label={intl.formatMessage(
                        { id: 'create-dialog.minimised.reopen' },
                        { title, context: detail ? `, ${detail}` : '' }
                    )}
                    onClick={onReopen}
                >
                    <span
                        aria-hidden
                        className={cn(
                            'flex size-6 shrink-0 items-center justify-center rounded-md max-sm:size-7 max-sm:rounded-[7px]',
                            tileClass
                        )}
                    >
                        <Icon className="size-3.5 max-sm:size-4" />
                    </span>
                    <span className="flex min-w-0 flex-1 items-baseline gap-2.5 overflow-hidden text-start max-sm:flex-col max-sm:gap-0">
                        <span className="shrink-0 font-semibold">{title}</span>
                        {detail ? (
                            <span className="text-muted-foreground text-meta max-sm:text-caption max-w-full min-w-0 truncate">
                                {detail}
                            </span>
                        ) : null}
                    </span>
                    <ChevronDownIcon className="text-foreground-secondary size-4 rotate-180 max-sm:size-[18px]" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="text-foreground-secondary max-sm:size-11"
                    onClick={request.close}
                >
                    <XIcon className="size-4 max-sm:size-[18px]" />
                    <span className="sr-only">
                        <FormattedMessage id="close" />
                    </span>
                </Button>
            </m.div>
        </>,
        document.body
    );
}
